import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../../api/client';
import {
  FiHome,
  FiLink,
  FiSettings,
  FiAward,
  FiActivity,
  FiFileText,
  FiCreditCard,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiChevronDown,
  FiChevronRight,
  FiUser,
  FiUsers,
  FiBarChart2,
  FiTrendingUp,
  FiCpu,
  FiRepeat,
  FiDollarSign,
  FiShare2,
  FiMessageCircle,
} from 'react-icons/fi';
import { FaRobot, FaTrophy } from 'react-icons/fa';
import { User } from '../../types';
import BrokerSwitcher from '../Trading/BrokerSwitcher';
import {
  isSubscriptionGatedTab,
  hidesBillingTab,
} from '../../utils/subscriptionAccess';
import { isDiscordPlan } from '../../utils/discordInvites';
import { showToast } from '../../utils/toast';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  user: User;
  isOpen?: boolean;
  onClose?: () => void;
  selectedBrokerId?: string | null;
  onBrokerChange?: (brokerId: string | null) => void;
  subscriptionResolved?: boolean;
  canAccessGatedTabs?: boolean;
}

interface Tab {
  id: string;
  icon: React.ReactNode;
  label: string;
  isRoute?: boolean; // If true, navigate to route instead of setting tab
  route?: string; // Route path if isRoute is true
  children?: Tab[]; // Nested tabs for expandable sections
  isExpandable?: boolean; // If true, shows conversations/items below
}

interface Conversation {
  _id: string;
  title: string;
  preview: string;
  messageCount: number;
  lastActivityAt: string;
  createdAt: string;
}

const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  user,
  isOpen = false,
  onClose,
  selectedBrokerId,
  onBrokerChange,
  subscriptionResolved = true,
  canAccessGatedTabs = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['subscriptions']));
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);

  // Listen for custom navigation events from BrokerSwitcher
  useEffect(() => {
    const handleNavigateToTab = (event: CustomEvent) => {
      const tabId = event.detail;
      const planNoneOnly = user.role === 'follower' && (!user.plan || user.plan === 'none');
      if (planNoneOnly && tabId !== 'pricing') {
        setActiveTab('pricing');
        navigate('/dashboard/pricing');
        return;
      }
      setActiveTab(tabId);
      if (!location.pathname.startsWith('/dashboard')) {
        navigate(`/dashboard/${tabId}`);
      }
    };

    window.addEventListener('navigateToTab', handleNavigateToTab as EventListener);
    return () => {
      window.removeEventListener('navigateToTab', handleNavigateToTab as EventListener);
    };
  }, [setActiveTab, navigate, location.pathname, canAccessGatedTabs, subscriptionResolved, user.role, user.plan]);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
      // Load conversations when AI Chatbot is expanded
      if (sectionId === 'ai-chat') {
        loadConversations();
      }
    }
    setExpandedSections(newExpanded);
  };

  // Load conversations for AI Chatbot
  const loadConversations = async () => {
    if (isLoadingConversations || conversations.length > 0) return; // Don't reload if already loaded

    try {
      setIsLoadingConversations(true);
      const response = await apiClient.get('/ai-chat/conversations');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  // Handle conversation click
  const handleConversationClick = (conversationId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setCurrentConversationId(conversationId);
    setActiveTab('ai-chat');
    navigate(`/dashboard/ai-chat?conversation=${conversationId}`);
    if (onClose) {
      onClose();
    }
  };

  // Handle new conversation
  const handleNewConversation = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentConversationId(null);
    setActiveTab('ai-chat');
    navigate('/dashboard/ai-chat');
    if (onClose) {
      onClose();
    }
  };

  // Delete conversation
  const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiClient.delete(`/ai-chat/conversations/${conversationId}`);
      setConversations(prev => prev.filter(c => c._id !== conversationId));
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // Load conversations when AI Chatbot tab is active and expanded
  useEffect(() => {
    if (activeTab === 'ai-chat' && expandedSections.has('ai-chat')) {
      loadConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, expandedSections]);

  // Get current conversation ID from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const convId = params.get('conversation');
    if (convId) {
      setCurrentConversationId(convId);
    }
  }, [location.search]);

  const getTabsForRole = (): Tab[] => {
    const baseItems: Tab[] = [
      { id: 'overview', icon: <FiHome className="h-5 w-5" />, label: 'Overview' },
    ];

    if (user.role === 'admin') {
      return [
        ...baseItems,
        {
          id: 'subscriptions',
          icon: <FiCreditCard className="h-5 w-5" />,
          label: 'Subscriptions',
          children: [
            { id: 'subscriptions-all', icon: <FiCreditCard className="h-4 w-4" />, label: 'All Users' },
            { id: 'subscriptions-active', icon: <FiCheckCircle className="h-4 w-4" />, label: 'Active' },
            { id: 'subscriptions-expired', icon: <FiXCircle className="h-4 w-4" />, label: 'Expired' },
            { id: 'subscriptions-none', icon: <FiClock className="h-4 w-4" />, label: 'No Subscription' },
          ]
        },
        { id: 'Eod Reports', icon: <FiBarChart2 className="h-5 w-5" />, label: 'EOD Reports' },
        // { id: 'admin-profits-losses', icon: <FiRepeat className="h-5 w-5" />, label: 'Profits / Losses' },
        // { id: 'master-fees', icon: <FiDollarSign className="h-5 w-5" />, label: 'Master Fees' },
        { id: 'affiliate-management', icon: <FiShare2 className="h-5 w-5" />, label: 'Affiliates' },
        { id: 'master-applications', icon: <FiAward className="h-5 w-5" />, label: 'Master Applications' },
        { id: 'master-following-report', icon: <FiUsers className="h-5 w-5" />, label: 'Master Following Report' },
        { id: 'openPositions', icon: <FiTrendingUp className="h-5 w-5" />, label: 'Open Positions' },
        // { id: 'calculator', icon: <FiTrendingUp className="h-5 w-5" />, label: 'Profit Calculator' },
        { id: 'terms', icon: <FiFileText className="h-5 w-5" />, label: 'Terms Templates' },
        { id: 'settings', icon: <FiSettings className="h-5 w-5" />, label: 'Settings' },
      ];
    }
    if (user.role === 'master' || user.role === 'master_pending') {
      return [
        ...baseItems,
        { id: 'connect', icon: <FiLink className="h-5 w-5" />, label: (user.hasBrokerageConnected || user.isAlpacaConnected) ? 'Brokerage Accounts' : 'Connect Brokerage' },
        { id: 'followers', icon: <FiUsers className="h-5 w-5" />, label: 'Followers', isRoute: true, route: '/followers' },
        { id: 'leaderboard', icon: <FaTrophy className="h-5 w-5" />, label: 'Traders Leaderboard' },
        { id: 'master-following-report', icon: <FiUsers className="h-5 w-5" />, label: 'Payout Report' },
        { id: 'openPositions', icon: <FiTrendingUp className="h-5 w-5" />, label: 'Open Positions' },
        { id: 'ai-chat', icon: <FiCpu className="h-5 w-5" />, label: 'AI Chatbot', isExpandable: true },
        { id: 'settings', icon: <FiSettings className="h-5 w-5" />, label: 'Settings' },
      ];
    }
    const hideBilling = hidesBillingTab(user.plan);
    const planNoneOnly = !user.plan || user.plan === 'none';
    // Discord + Billing only for Growth / General / Premium (pro)
    const showDiscord = !hideBilling && isDiscordPlan(user.plan);

    // Users with no plan can only access Billing + Leaderboard
    if (planNoneOnly) {
      return [
        { id: 'pricing', icon: <FiDollarSign className="h-5 w-5" />, label: 'Billing' },
      ];
    }

    return [
      ...baseItems,
      { id: 'connect', icon: <FiLink className="h-5 w-5" />, label: (user.hasBrokerageConnected || user.isAlpacaConnected) ? 'Brokerage Accounts' : 'Connect Brokerage' },
      { id: 'masters', icon: <FiAward className="h-5 w-5" />, label: 'Master Traders' },
      { id: 'leaderboard', icon: <FaTrophy className="h-5 w-5" />, label: 'Traders Leaderboard' },
      { id: 'bots-leaderboard', icon: <FaRobot className="h-5 w-5" />, label: 'Bots Leaderboard' },
      { id: 'activeCopies', icon: <FiActivity className="h-5 w-5" />, label: 'Active Copies' },
      { id: 'ai-chat', icon: <FiCpu className="h-5 w-5" />, label: 'AI Chatbot', isExpandable: true },
      { id: 'calculator', icon: <FiTrendingUp className="h-5 w-5" />, label: 'Profit Calculator' },
      ...(!hideBilling
        ? [{ id: 'pricing', icon: <FiDollarSign className="h-5 w-5" />, label: 'Billing' }]
        : []),
      ...(showDiscord
        ? [{ id: 'discord', icon: <FiMessageCircle className="h-5 w-5" />, label: 'Discord' }]
        : []),
      { id: 'settings', icon: <FiSettings className="h-5 w-5" />, label: 'Settings' },
    ];
  };

  const visibleTabs = getTabsForRole().filter((tab) => {
    if (!isSubscriptionGatedTab(tab.id)) return true;
    if (!subscriptionResolved) return true;
    return canAccessGatedTabs;
  });

  const handleClick = (tab: Tab) => {
    if (tab.isRoute && tab.route) {
      navigate(tab.route);
      if (onClose) {
        onClose();
      }
    } else {
      // If we're on a route page (like /community), navigate to dashboard tab page.
      if (!location.pathname.startsWith('/dashboard')) {
        navigate(`/dashboard/${tab.id}`);
      } else {
        setActiveTab(tab.id);
        navigate(`/dashboard/${tab.id}`);
      }
      if (onClose) {
        onClose();
      }
    }
  };

  const renderTab = (tab: Tab, isChild: boolean = false) => {
    const hasChildren = tab.children && tab.children.length > 0;
    const isExpandable = tab.isExpandable;
    const isExpanded = expandedSections.has(tab.id);
    const isActive = tab.isRoute
      ? location.pathname === tab.route
      : location.pathname.startsWith('/dashboard') && activeTab === tab.id;
    const isChildActive = hasChildren && tab.children?.some(child =>
      location.pathname.startsWith('/dashboard') && activeTab === child.id
    );

    // Handle expandable AI Chatbot with conversations
    if (isExpandable && tab.id === 'ai-chat') {
      const isChatActive = location.pathname.startsWith('/dashboard') && activeTab === 'ai-chat';
      return (
        <div key={tab.id}>
          <button
            onClick={() => {
              toggleSection(tab.id);
              if (!isChatActive) {
                handleClick(tab);
              }
            }}
            className={`w-full flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl transition-all duration-200 text-left ${isChatActive
              ? 'sidebar-active font-semibold'
              : 'text-foreground hover:bg-primary/10 hover:text-primary'
              }`}
          >
            <div className="flex items-center gap-3">
              <span className={isChatActive ? 'text-blue-200' : 'text-muted'}>{tab.icon}</span>
              <span className="text-sm sm:text-base">{tab.label}</span>
            </div>
            {isExpanded ? (
              <FiChevronDown className="h-4 w-4 text-muted" />
            ) : (
              <FiChevronRight className="h-4 w-4 text-muted" />
            )}
          </button>

          {isExpanded && (
            <div className="ml-3 mt-1 space-y-0.5 border-l border-border pl-3">
              <button
                onClick={() => {
                  if (activeTab !== 'ai-chat' || !location.pathname.startsWith('/dashboard')) {
                    setActiveTab('ai-chat');
                    navigate('/dashboard/ai-chat');
                  }
                  if (onClose) onClose();
                  // Dispatch event for StockAnalysisChatbot to open history sidebar (small delay so it can mount if tab just switched)
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('openChatHistory'));
                  }, 100);
                }}
                className="w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-foreground transition-all duration-200 hover:bg-primary/10 hover:text-primary"
              >
                Chat History
              </button>
            </div>
          )}
        </div>
      );
    }

    if (hasChildren) {
      return (
        <div key={tab.id}>
          <button
            onClick={() => toggleSection(tab.id)}
            className={`w-full flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl transition-all duration-200 text-left ${isChildActive
              ? 'sidebar-active font-semibold'
              : 'text-foreground hover:bg-primary/10 hover:text-primary'
              }`}
          >
            <div className="flex items-center gap-3">
              <span className={isChildActive ? 'text-blue-200' : 'text-muted'}>{tab.icon}</span>
              <span className="text-sm sm:text-base">{tab.label}</span>
            </div>
            {isExpanded ? (
              <FiChevronDown className="h-4 w-4 text-muted" />
            ) : (
              <FiChevronRight className="h-4 w-4 text-muted" />
            )}
          </button>

          {isExpanded && (
            <div className="ml-3 mt-1 space-y-0.5 border-l border-border pl-3">
              {tab.children?.map(child => renderTab(child, true))}
            </div>
          )}
        </div>
      );
    }

    return (
      <button
        key={tab.id}
        onClick={() => handleClick(tab)}
        className={`w-full flex items-center gap-3 ${isChild ? 'px-3 py-2' : 'px-3.5 py-3'} rounded-xl transition-all duration-200 text-left ${isActive
          ? 'sidebar-active font-semibold'
          : 'text-foreground hover:bg-primary/10 hover:text-primary'
          }`}
      >
        <span className={isActive ? 'text-blue-200' : 'text-muted'}>{tab.icon}</span>
        <span className={isChild ? 'text-sm' : 'text-sm sm:text-base'}>{tab.label}</span>
      </button>
    );
  };

  // Show broker switcher for users with broker connections (not admin, not plan-none)
  const planNoneOnly = user.role === 'follower' && (!user.plan || user.plan === 'none');
  const showBrokerSwitcher =
    !planNoneOnly &&
    user.role !== 'admin' &&
    (user.hasBrokerageConnected || user.isAlpacaConnected);
  const isPartnerMode =
    location.pathname.startsWith('/dashboard/affiliate') && activeTab === 'affiliate';
  const canUseClientMode = user.affiliateClientAccess !== false;
  const partnerSections = [
    { id: 'dashboard', label: 'Dashboard', icon: <FiHome className="h-5 w-5" /> },
    { id: 'referrals', label: 'Referrals', icon: <FiUsers className="h-5 w-5" /> },
    { id: 'links', label: 'Links', icon: <FiLink className="h-5 w-5" /> },
    { id: 'commissions', label: 'Commissions', icon: <FiDollarSign className="h-5 w-5" /> },
    { id: 'payouts', label: 'Payouts', icon: <FiCreditCard className="h-5 w-5" /> },
    { id: 'statistics', label: 'Statistics', icon: <FiBarChart2 className="h-5 w-5" /> },
  ];
  const activePartnerSection =
    location.hash.replace('#affiliate-', '') || 'dashboard';

  const openPartnerSection = (sectionId: string) => {
    setActiveTab('affiliate');
    navigate(
      sectionId === 'dashboard'
        ? '/dashboard/affiliate'
        : `/dashboard/affiliate#affiliate-${sectionId}`,
    );
    window.setTimeout(() => {
      const targetId =
        sectionId === 'dashboard' ? 'affiliate-dashboard' : `affiliate-${sectionId}`;
      document.getElementById(targetId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
    if (onClose) onClose();
  };

  return (
    <aside
      className={`app-sidebar fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] transform flex-col overflow-hidden border-r transition-transform duration-300 lg:z-40 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
    >
      <div className="relative flex h-24 shrink-0 items-center gap-3.5 overflow-hidden border-b border-border px-4">
        <div className="pointer-events-none absolute -left-8 -top-12 h-36 w-36 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/30 to-blue-500/10 p-0 ring-1 ring-primary/30 shadow-[0_0_28px_rgba(37,99,235,0.35)]">
          <img
            src="/trade-pilot-logo.png"
            alt="Trade Pilot"
            className="h-full w-full rounded-xl object-cover"
          />
        </div>
        <div className="relative min-w-0">
          <span className="title-brand-gradient block whitespace-nowrap text-2xl font-extrabold leading-tight tracking-[-0.03em]">
            Trade Pilot
          </span>
          <span className="mt-1 block text-[9px] font-bold uppercase tracking-[0.18em] text-muted">
            AI Copy Trading
          </span>
        </div>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-6">
        {/* Broker Switcher - Only show for users with broker connections */}
        {!isPartnerMode && showBrokerSwitcher && onBrokerChange && (
          <div className="pb-4 mb-2 border-b border-border">
            <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2 px-2">
              Active Broker
            </p>
            <BrokerSwitcher
              selectedBrokerId={selectedBrokerId || null}
              onBrokerChange={onBrokerChange}
            />
          </div>
        )}

        {isPartnerMode ? (
          <div className="space-y-2">
            {partnerSections.map((section) => {
              const isActive = activePartnerSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => openPartnerSection(section.id)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3.5 py-3 text-left transition-all duration-200 ${isActive
                    ? 'sidebar-active font-semibold'
                    : 'text-foreground hover:bg-primary/10 hover:text-primary'
                    }`}
                >
                  <span className={isActive ? 'text-blue-200' : 'text-muted'}>
                    {section.icon}
                  </span>
                  <span className="text-sm sm:text-base">{section.label}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {visibleTabs.map((tab) => renderTab(tab))}
          </div>
        )}
      </nav>

      {user.ableToAffiliate && user.role !== 'admin' && (
        <div className="shrink-0 border-t border-border bg-surface-elevated p-4">
          <p className="mb-3 px-1 text-xs font-bold uppercase tracking-[0.16em] text-muted">
            Switch Modes
          </p>
          <div className={`grid gap-2 rounded-xl border border-border bg-surface p-1.5 ${canUseClientMode ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {canUseClientMode && (
              <button
                type="button"
                aria-pressed={!isPartnerMode}
                onClick={() => {
                  setActiveTab('overview');
                  navigate('/dashboard/overview');
                  if (onClose) onClose();
                }}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all ${!isPartnerMode
                  ? 'bg-primary text-white shadow-sm shadow-primary/20'
                  : 'text-muted hover:bg-surface-elevated hover:text-foreground'
                  }`}
              >
                <FiUser className="h-4 w-4" />
                Client
              </button>
            )}
            <button
              type="button"
              aria-pressed={isPartnerMode}
              onClick={() => {
                setActiveTab('affiliate');
                navigate('/dashboard/affiliate');
                if (onClose) onClose();
              }}
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all ${isPartnerMode
                ? 'bg-primary text-white shadow-sm shadow-primary/20'
                : 'text-muted hover:bg-surface-elevated hover:text-foreground'
                }`}
            >
              <FiUsers className="h-4 w-4" />
              Partner
            </button>
          </div>
          {!canUseClientMode && (
            <p className="mt-2 px-1 text-[11px] text-muted">
              Client mode is disabled for this account.
            </p>
          )}
        </div>
      )}
    </aside>
  );
};

export default Sidebar;